import React, { PureComponent } from 'react'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import get from 'lodash/get'
import { utils, styles, translations } from 'gipsy-misc'
import { AnimateHeight } from 'gipsy-ui'
import { tags as tagsApi } from 'gipsy-api'
import { getListTotalDuration } from 'features/list/utils'
import { groups } from 'features/list/constants.json'

import container from './container'
import ListHeader from 'features/list/components/listHeader'
import AllDayEvents from 'features/todolist/components/allDayEvents'
import TagFilters from 'features/todolist/components/tagFilters'
import InlineAddTask from 'features/list/components/inlineAddTask'
import Line from 'features/list/components/line'
import TaskPanel from 'features/taskPanel'
import { getDisplayNamesFromNotifications, getUniqueTasksIdsFromNotifications } from 'features/notifications/utils'
import {
  onMouseEnter,
  onMouseLeave,
  onDragStart as onDragStartGeneric,
  onDragEnd,
  onDrop as onDropGeneric,
  onDropComplete
} from 'features/draganddrop/utils'
import { getSrc } from 'utils/image'

import DraggableList from 'features/list/components/draggableList'

class TodolistPage extends PureComponent {
  static propTypes = {
    todoItems: PropTypes.object,
    actions: PropTypes.object.isRequired,
    onComplete: PropTypes.func.isRequired,
    onClick: PropTypes.func.isRequired,
    onChange: PropTypes.func.isRequired,
    highlightIds: PropTypes.array,
    shouldReschedule: PropTypes.bool.isRequired,
    reorderTaskNotifs: PropTypes.array
  }
  constructor(props) {
    super(props)

    this.state = {
      dragging: false,
      tasksToHiglight: new Set(),
      todoItems: props.todoItems,
      selectedTagFilter: 'all'
    }

    this.onMouseEnter = onMouseEnter.bind(this)
    this.onMouseLeave = onMouseLeave.bind(this)
    this.onDragEnd = onDragEnd.bind(this)
    this.onDragStartGeneric = onDragStartGeneric.bind(this)
    this.onDropGeneric = onDropGeneric.bind(this)
    this.onDropComplete = onDropComplete.bind(this)
  }

  componentDidMount = () => {
    const { reorderTaskNotifs } = this.props
    if (reorderTaskNotifs.length > 0) {
      this.showReorderTaskPopup(reorderTaskNotifs)
    }

    tagsApi.get().then(result =>
      this.setState({
        tags: result
      })
    )
  }

  componentDidUpdate = prevProps => {
    const newNotifs = this.props.reorderTaskNotifs
    if (newNotifs.length > 0) {
      const oldNotifs = prevProps.reorderTaskNotifs
      const oldTasksIdsSet = getUniqueTasksIdsFromNotifications(oldNotifs)
      const newTasksIdsSet = getUniqueTasksIdsFromNotifications(newNotifs)
      if (!this.areSetsEqual(oldTasksIdsSet, newTasksIdsSet)) {
        this.showReorderTaskPopup(newNotifs)
      }
    }

    if (prevProps.todoItems !== this.props.todoItems) {
      this.setState({ todoItems: this.props.todoItems })
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
  }

  showReorderTaskPopup = reorderTaskNotifs => {
    const displayNamesDict = getDisplayNamesFromNotifications(reorderTaskNotifs)
    const displayNames = utils.user.getDisplayNamesFromObject(displayNamesDict)
    let message = translations.notifications.reorderTask.text
    message = utils.string.capitalize(message.replace('[displayNames]', displayNames))

    this.props.actions.openPopup({
      message: message,
      button: translations.notifications.reorderTask.button,
      action: this.clickShowNotifs,
      onClose: this.props.actions.markReorderTaskNotifsAsRead
    })
  }

  areSetsEqual = (as, bs) => {
    if (as.size !== bs.size) return false
    for (var a of as) if (!bs.has(a)) return false
    return true
  }

  onDragStart = data => {
    this.onDragStartGeneric(data, this.props.actions.cancelGetTodolist)
  }

  onDropCallback = async actionParam => {
    try {
      const code = await this.props.actions.dragAndDrop(actionParam)
      if (code) {
        const message = get(translations.dragAndDrop, code)
        this.props.actions.showSnackbar({ message: message, time: 5 * 1000 })
      }
    } catch (err) {
      console.error(err)
    }
  }

  onDrop = result => {
    this.onDropGeneric(result, this.onDropCallback)
  }

  clickShowNotifs = () => {
    const { reorderTaskNotifs } = this.props
    const tasksIdsSet = getUniqueTasksIdsFromNotifications(reorderTaskNotifs)
    this.setState({ tasksToHiglight: tasksIdsSet })
    this.props.actions.markReorderTaskNotifsAsRead()
    this.props.actions.closePopup()
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => this.removeFromState(tasksIdsSet), 30 * 1000)
  }

  removeFromState = async tasksIdsSet => {
    let { tasksToHiglight } = this.state
    for (let id of tasksIdsSet) {
      tasksToHiglight.delete(id)
    }
    this.setState({ tasksToHiglight })
  }

  handleSelectFilter = tagId => {
    this.setState({ selectedTagFilter: tagId })
  }

  getFilteredTodoItems = () => {
    const { todoItems } = this.props
    const { selectedTagFilter } = this.state
    if (selectedTagFilter === 'all') {
      return todoItems
    }

    const res = {}

    for (let group of groups) {
      const list = todoItems[group].list
      const filteredList = list.filter(
        item => item.tags && utils.array.getObjectIndexInArray(item.tags, { id: selectedTagFilter }, ['id']) !== -1
      )
      res[group] = { list: filteredList }
    }

    return res
  }

  render() {
    const { highlightIds, onComplete, onClick, onChange, shouldReschedule } = this.props
    const { dragging, tasksToHiglight, tags } = this.state

    const toHighlight = highlightIds.concat([...tasksToHiglight])

    const filteredTodoItems = this.getFilteredTodoItems()

    return (
      <Page id="todo-page">
        <TagFilters tags={tags} onSelectFilter={this.handleSelectFilter} />
        {filteredTodoItems &&
          groups.map((group, index) => {
            const list = filteredTodoItems[group].list
            const totalDuration = getListTotalDuration(list)

            const shouldHide = utils.datetime.isGroupUpcoming(group) && !list.length
            if (utils.datetime.isGroupOnboarding(group) && list.length) {
              return (
                <Group key={group} className="group" onboarding={true}>
                  <LogoContainer>
                    <img src={getSrc('/images/gipsybot-white.svg')} alt="logo" />
                  </LogoContainer>
                  <ListHeader
                    group={translations.onboarding.tasks.sectionTitle}
                    totalDuration={totalDuration}
                    noCapitalize={true}
                  />
                  <ListBody>
                    {list.map((item, index) => (
                      <Line
                        hideDraggableCell={true}
                        isInOnboarding={true}
                        item={item}
                        key={`${item.id}-${index}`}
                        onComplete={onComplete}
                        onClick={onClick}
                        onChange={onChange}
                      />
                    ))}
                  </ListBody>
                </Group>
              )
            }

            if (!utils.datetime.isGroupOnboarding(group)) {
              return (
                <AnimateHeight key={group} duration={300} height={!shouldHide ? 'auto' : 0}>
                  <Group className="group" empty={shouldHide ? true : false} dragging={dragging}>
                    {group === 'today' ? (
                      <ListHeader group={group} totalDuration={totalDuration} shouldReschedule={shouldReschedule} />
                    ) : (
                      <ListHeader group={group} totalDuration={totalDuration} />
                    )}
                    {get(filteredTodoItems, `${group}.allDayEvents.length`) ? (
                      <AllDayEvents events={filteredTodoItems[group].allDayEvents} />
                    ) : (
                      ''
                    )}
                    <DraggableList
                      dragging={dragging}
                      onDragStart={this.onDragStart}
                      onDragEnd={this.onDragEnd}
                      onMouseEnter={this.onMouseEnter}
                      onMouseLeave={this.onMouseLeave}
                      onDrop={this.onDrop}
                      group={group}
                      list={list}
                      highlightIds={toHighlight}
                      onClick={onClick}
                      onChange={onChange}
                      onComplete={onComplete}
                    />
                    {!utils.datetime.isGroupUpcoming(group) && (
                      <InlineAddTask hide={dragging} group={group} withTags={true} withAssignee={true} />
                    )}
                  </Group>
                </AnimateHeight>
              )
            }
            return null
          })}
        <TaskPanel />
      </Page>
    )
  }
}

const Page = styled.div`
  min-width: 320px;
`

const Group = styled.div`
  position: relative;
  padding-bottom: 0;
  transition: opacity 0.2s;
  opacity: 0;
  ${({ empty }) =>
    !empty &&
    css`
      opacity: 1;
      margin-bottom: 50px;
    `}

  ${({ dragging }) =>
    dragging &&
    css`
      opacity: 1;
    `}

  ${({ onboarding }) =>
    onboarding &&
    css`
      padding-top: 50px;
      margin-top: 50px;
      padding-left: 35px;
      padding-right: 35px;
      padding-bottom: 20px;
      background-color: white;
      border-radius: 2px 2px 8px 8px;
      box-shadow: 0 16px 48px 0 ${styles.colors.lightGrey};
    `}
`

const ListBody = styled.div``

const LogoContainer = styled.div`
  height: 90px;
  width: 90px;
  top: -50px;
  left: 43px;
  position: absolute;
`

export default container(TodolistPage)



// WEBPACK FOOTER //
// ./src/pages/todolist/todoPage/index.js